/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BinaryWriter } from "../utils/binary-writer";
import { METAPLEX_PROGRAM_ID } from "./solana";
import { PublicKey } from "@solana/web3.js";

/**
 * Simulates the creation of an instruction to create a Metaplex Metadata Account.
 * This is a simplified client-side representation.
 */
export const createMetadataInstruction = (
    mint: PublicKey,
    payer: PublicKey,
    updateAuthority: PublicKey,
    name: string,
    symbol: string,
    uri: string,
    sellerFeeBasisPoints: number = 0
) => {
    // 1. Find PDA for Metadata Account
    const [metadataAddress] = PublicKey.findProgramAddressSync(
        [
            new TextEncoder().encode("metadata"),
            new PublicKey(METAPLEX_PROGRAM_ID).toBuffer(),
            mint.toBuffer(),
        ],
        new PublicKey(METAPLEX_PROGRAM_ID)
    );

    // 2. Serialize Data for "CreateMetadataAccountV3" (Instruction Discriminator: 33)
    const writer = new BinaryWriter();
    writer.writeu8(33); // Instruction Discriminator

    // DataV2 struct
    writer.writeString(name);
    writer.writeString(symbol);
    writer.writeString(uri);
    writer.writeu16(sellerFeeBasisPoints);
    
    // Creators (Option<Vec<Creator>>) - null for this version
    writer.writeu8(0); // Option None
    
    // Collection (Option<Collection>) - null
    writer.writeu8(0); // Option None

    // Uses (Option<Uses>) - null
    writer.writeu8(0); // Option None

    // isMutable (bool)
    writer.writeu8(1); // True

    // collectionDetails (Option<CollectionDetails>) - None
    writer.writeu8(0); // Option None

    const data = writer.toBuffer();

    const keys = [
        { pubkey: metadataAddress.toBase58(), isSigner: false, isWritable: true },
        { pubkey: mint.toBase58(), isSigner: false, isWritable: false },
        { pubkey: payer.toBase58(), isSigner: true, isWritable: false }, // Mint Authority
        { pubkey: payer.toBase58(), isSigner: true, isWritable: true },  // Payer
        { pubkey: updateAuthority.toBase58(), isSigner: false, isWritable: false },
        { pubkey: "11111111111111111111111111111111", isSigner: false, isWritable: false }, // System Program
        { pubkey: "SysvarRent111111111111111111111111111111111", isSigner: false, isWritable: false }, // Rent
    ];

    return {
        programId: METAPLEX_PROGRAM_ID,
        keys,
        data: data
    };
};

/**
 * Simulates the creation of an instruction to create a Metaplex Master Edition Account.
 */
export const createMasterEditionInstruction = (
    mint: PublicKey,
    updateAuthority: PublicKey,
    mintAuthority: PublicKey,
    payer: PublicKey,
    maxSupply: number | null = 0
) => {
    // 1. Find PDAs
    const [editionAddress] = PublicKey.findProgramAddressSync(
        [
            new TextEncoder().encode("metadata"),
            new PublicKey(METAPLEX_PROGRAM_ID).toBuffer(),
            mint.toBuffer(),
            new TextEncoder().encode("edition"),
        ],
        new PublicKey(METAPLEX_PROGRAM_ID)
    );

    const [metadataAddress] = PublicKey.findProgramAddressSync(
        [
            new TextEncoder().encode("metadata"),
            new PublicKey(METAPLEX_PROGRAM_ID).toBuffer(),
            mint.toBuffer(),
        ],
        new PublicKey(METAPLEX_PROGRAM_ID)
    );

    // 2. Serialize Data for "CreateMasterEditionV3" (Instruction Discriminator: 17)
    const writer = new BinaryWriter();
    writer.writeu8(17); 
    
    if (maxSupply !== null) {
        writer.writeu8(1); // Option Some
        writer.writeu64(maxSupply);
    } else {
        writer.writeu8(0); // Option None
    }

    const data = writer.toBuffer();

    const keys = [
        { pubkey: editionAddress.toBase58(), isSigner: false, isWritable: true },
        { pubkey: mint.toBase58(), isSigner: false, isWritable: true },
        { pubkey: updateAuthority.toBase58(), isSigner: true, isWritable: false },
        { pubkey: mintAuthority.toBase58(), isSigner: true, isWritable: false },
        { pubkey: payer.toBase58(), isSigner: true, isWritable: true },
        { pubkey: metadataAddress.toBase58(), isSigner: false, isWritable: false },
        { pubkey: "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA", isSigner: false, isWritable: false }, // Token Program
        { pubkey: "11111111111111111111111111111111", isSigner: false, isWritable: false }, // System Program
        { pubkey: "SysvarRent111111111111111111111111111111111", isSigner: false, isWritable: false }, // Rent
    ];

    return {
        programId: METAPLEX_PROGRAM_ID,
        keys,
        data: data
    };
}